import React, { useState, useRef } from "react";
import EssentialInfoStyle from "../../../assets/stylesheets/farm-detail/sections/EssentialInfo.module.css";
import FarmDetailStyle from "../../../assets/stylesheets/farm-detail/FarmDetailPage.module.css";
import { ChevronDown } from "lucide-react";
import OperatingHours from "../../farm-detail/sections/OperatingHours";
import {
  Address,
  Social,
  Website
} from "../../../assets/logo/OverviewIcon";

const ESSENTIAL_INFO_TITLE = "Farm essentials";
const OPENING_HOURS_BUTTON_TEXT = "View more information";
const OPENING_HOURS_TITLE = "Opening hours";

function getDomainOnly(url) {
  try {
    const { hostname } = new URL(url);
    return hostname.replace(/^www\./, '');
  } catch (e) {
    return url;
  }
}

function getSocialHandle(url) {
  try {
    const { pathname } = new URL(url);
    const handle = pathname.split("/").filter(Boolean)[0];
    return handle ? `@${handle}` : url;
  } catch (e) {
    return url;
  }
}

// Mobile-specific Operating Hours Component
function MobileOperatingHours({ hours }) {
  function formatTo12Hour(time) {
    if (!time) return "";
    const [hourStr, minuteStr] = time.split(":");
    let hour = parseInt(hourStr, 10);
    const minute = minuteStr;
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12; // 0 → 12, 13 → 1
    return `${hour}:${minute} ${ampm}`;
  }

  function isNowOpen(hours) {
    const now = new Date();
    const today = now.toLocaleString('en-US', { weekday: 'short' }); // e.g. "Mon", "Tue"
    const currentTime = now.toTimeString().slice(0, 5); // "HH:mm"

    const todayEntry = hours.find(([day]) => day.startsWith(today));
    if (!todayEntry || !todayEntry[1]) return false;

    const [openRaw, closeRaw] = todayEntry[1].split(" - ") || [];
    if (!openRaw || !closeRaw || openRaw === "null" || closeRaw === "null") return false;

    const open = openRaw.slice(0, 5);
    const close = closeRaw.slice(0, 5);

    return open <= currentTime && currentTime <= close;
  }

  const openNow = isNowOpen(hours);

  return (
    <div className={EssentialInfoStyle["essential-mobile-hours"]}>
      <div className={EssentialInfoStyle["essential-mobile-hours-header"]}>
        <span className={EssentialInfoStyle["essential-mobile-hours-title"]}>
          {OPENING_HOURS_TITLE}
        </span>
        <span
          className={`${EssentialInfoStyle["essential-mobile-hours-status"]} ${
            openNow ? EssentialInfoStyle["open"] : EssentialInfoStyle["closed"]
          }`}
        >
          {openNow ? "Open now" : "Closed"}
        </span>
      </div>
      <ul className={EssentialInfoStyle["essential-mobile-hours-list"]}>
        {hours.map(([day, time], idx) => {
          const isNullRange = /^null\s*-\s*null$/.test(time);
          const isZeroRange = time?.trim() === "00:00:00 - 00:00:00";
          const displayTime =
            (!time || isNullRange || isZeroRange)
              ? "Closed"
              : (() => {
                const [open, close] = time.split(" - ");
                const openFormatted = formatTo12Hour(open.slice(0, 5));
                const closeFormatted = formatTo12Hour(close.slice(0, 5));
                return `${openFormatted} - ${closeFormatted}`;
              })();

          return (
            <li key={idx} className={EssentialInfoStyle["essential-mobile-hours-item"]}>
              <span className={EssentialInfoStyle["essential-mobile-hours-day"]}>{day}</span>
              <span className={EssentialInfoStyle["essential-mobile-hours-time"]}>{displayTime}</span>
            </li>
          );
        })}
      </ul>
      <p className={EssentialInfoStyle["essential-mobile-hours-disclaimer"]}>
        Hours or services may differ.
      </p>
    </div>
  );
}

export default function EssentialInfo({ address, web, social, hours, isMobile }) {
  const [isHoursOpen, setIsHoursOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const closeTimeout = useRef();

  // Modal close handler with animation
  const handleCloseModal = () => {
    setClosing(true);
    closeTimeout.current = setTimeout(() => {
      setIsHoursOpen(false);
      setClosing(false);
    }, 200);
  };

  // Prevent background scroll when modal is open
  React.useEffect(() => {
    if (isHoursOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      if (closeTimeout.current) clearTimeout(closeTimeout.current);
    };
  }, [isHoursOpen]);

  return (
    <>
      {/* Contact Info */}
      <section className={EssentialInfoStyle["essential-info-container"]}>
        <h3 className={FarmDetailStyle["farmDetail-title"]}>
          {ESSENTIAL_INFO_TITLE}
        </h3>
        <div className={EssentialInfoStyle["essential-contact-info"]}>
          <div className={EssentialInfoStyle["essential-contact-row"]}>
            <div className={EssentialInfoStyle["essential-contact-list-title"]}>
              <Address className={EssentialInfoStyle["essential-info-icon"]} />
              <strong className={EssentialInfoStyle["essential-contact-label"]}>Address:</strong>
            </div>
            <span className={EssentialInfoStyle["essential-contact-text"]}>{address}</span>
          </div>
          <div className={EssentialInfoStyle["essential-contact-row"]}>
            <div className={EssentialInfoStyle["essential-contact-list-title"]}>
              <Website className={EssentialInfoStyle["essential-info-icon"]} />
              <strong className={EssentialInfoStyle["essential-contact-label"]}>Website:</strong>
            </div>
            <a href={web} className={EssentialInfoStyle["essential-contact-text link"]}>
              {getDomainOnly(web)}
            </a>
          </div>
          <div className={EssentialInfoStyle["essential-contact-row"]}>
            <div className={EssentialInfoStyle["essential-contact-list-title"]}>
              <Social className={EssentialInfoStyle["essential-info-icon"]} />
              <strong className={EssentialInfoStyle["essential-contact-label"]}>Socials:</strong>
            </div>
            <a href={social} className={EssentialInfoStyle["essential-contact-text link"]}>
              {getSocialHandle(social)}
            </a>
          </div>
        </div>
        {/* Opening Hours (mobile only) */}
        {isMobile && (
          <div className={EssentialInfoStyle["essential-open-hours-btn"]}>
            <button onClick={() => setIsHoursOpen(true)} className={EssentialInfoStyle["essential-open-hours-btn"]}>
              <span className={EssentialInfoStyle["essential-open-hours-btn-text"]}>{OPENING_HOURS_BUTTON_TEXT}</span>
            </button>
          </div>
        )}
        {isMobile && isHoursOpen && (
          <div className={EssentialInfoStyle["essential-slideup-overlay"]} onClick={handleCloseModal}>
            <div
              className={[
                EssentialInfoStyle["essential-slideup-panel"],
                closing ? EssentialInfoStyle["essential-slideDown"] : ''
              ].join(' ')}
              role="dialog"
              aria-modal="true"
              tabIndex={-1}
              onClick={e => e.stopPropagation()}
            >
              <div className={EssentialInfoStyle["essential-slideup-header"]}>
                <button onClick={handleCloseModal}>
                  <ChevronDown size={24} />
                </button>
                <span>{ESSENTIAL_INFO_TITLE}</span>
              </div>
              <div className={EssentialInfoStyle["essential-slideup-container"]}>
                {/* <div className={EssentialInfoStyle["essential-slideup-spacer"]}></div> */}
                <div className={EssentialInfoStyle["essential-slideup-body"]}>
                  <MobileOperatingHours hours={hours} />
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
